import React, { useState } from 'react';
import '../css/LanguageTranslator.css';

export default function TranslatorPage() {
  const [fromLanguage, setFromLanguage] = useState('en');
  const [toLanguages, setToLanguages] = useState(['da', 'sv', 'de', 'es']);
  const [inputText, setInputText] = useState('');
  const [translations, setTranslations] = useState({});
  const [loading, setLoading] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [hasConverted, setHasConverted] = useState(false);

  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'da', name: 'Danish', flag: '🇩🇰' },
    { code: 'sv', name: 'Swedish', flag: '🇸🇪' },
    { code: 'de', name: 'German', flag: '🇩🇪' },
    { code: 'es', name: 'Spanish', flag: '🇪🇸' },
    { code: 'it', name: 'Italian', flag: '🇮🇹' },
    { code: 'pt', name: 'Portuguese', flag: '🇵🇹' },
    { code: 'fr', name: 'French', flag: '🇫🇷' },
    { code: 'nl', name: 'Dutch', flag: '🇳🇱' },
    { code: 'ru', name: 'Russian', flag: '🇷🇺' },
    { code: 'ja', name: 'Japanese', flag: '🇯🇵' },
    { code: 'zh', name: 'Chinese', flag: '🇨🇳' },
    { code: 'ko', name: 'Korean', flag: '🇰🇷' },
    { code: 'ar', name: 'Arabic', flag: '🇸🇦' },
    { code: 'hi', name: 'Hindi', flag: '🇮🇳' },
    { code: 'tr', name: 'Turkish', flag: '🇹🇷' },
    { code: 'id', name: 'Indonesian', flag: '🇮🇩' },
    { code: 'th', name: 'Thai', flag: '🇹🇭' },
    { code: 'vi', name: 'Vietnamese', flag: '🇻🇳' },
    { code: 'ms', name: 'Malay', flag: '🇲🇾' },
    { code: 'fil', name: 'Filipino', flag: '🇵🇭' },
    { code: 'he', name: 'Hebrew', flag: '🇮🇱' },
    { code: 'ne', name: 'Nepali', flag: '🇳🇵' },
  ];

  // Get language info
  const getLanguageInfo = (code) => {
    return languages.find(lang => lang.code === code);
  };

  // Get available languages for "To" dropdown (excluding already selected)
  const getAvailableLanguages = () => {
    return languages.filter(lang => lang.code !== fromLanguage && !toLanguages.includes(lang.code));
  };

  // Add language to "To" languages
  const addLanguage = (langCode) => {
    if (!toLanguages.includes(langCode)) {
      setToLanguages([...toLanguages, langCode]);
    }
  };

  // Remove language from "To" languages
  const removeLanguage = (langCode) => {
    setToLanguages(toLanguages.filter(code => code !== langCode));
  };

  // Translate text using Google Translate API (via backend proxy)
  const translateWithGoogle = async (text, targetLang) => {
    // Using Google Translate's free endpoint (no API key needed)
    // This endpoint works from the browser
    const url = `https://translate.googleapis.com/translate_a/single?client=gtx&sl=${fromLanguage}&tl=${targetLang}&dt=t&q=${encodeURIComponent(text)}`;
    
    try {
      const response = await fetch(url);
      const data = await response.json();
      
      // Google returns a complex nested array structure
      // Format: [[["translated text","original text",null,null,1]],...]
      if (data && data[0] && data[0][0] && data[0][0][0]) {
        // Join all segments (for long texts that come in multiple parts)
        const translatedText = data[0].map(segment => segment[0]).join('');
        return translatedText;
      }
      return 'Translation failed';
    } catch (error) {
      console.error('Google Translate error:', error);
      return 'Translation failed';
    }
  };

  // Translate text
  const handleConvert = async () => {
    if (!inputText.trim()) {
      alert('Please enter text to translate');
      return;
    }

    if (toLanguages.length === 0) {
      alert('Please select at least one language to translate to');
      return;
    }

    setLoading(true);
    const newTranslations = {};

    try {
      // Translate to all selected languages in parallel
      const translationPromises = toLanguages.map(async (langCode) => {
        try {
          const translatedText = await translateWithGoogle(inputText, langCode);
          return { langCode, translatedText };
        } catch (error) {
          console.error(`Error translating to ${langCode}:`, error);
          return { langCode, translatedText: 'Translation failed' };
        }
      });

      const results = await Promise.all(translationPromises);
      
      results.forEach(result => {
        newTranslations[result.langCode] = result.translatedText;
      });
      
      setTranslations(newTranslations);
      setHasConverted(true);
    } catch (error) {
      console.error('Translation error:', error);
      alert('Translation failed. Please try again.');
    }

    setLoading(false);
  };

  // Clear everything
  const handleClear = () => {
    setInputText('');
    setTranslations({});
    setHasConverted(false);
    setCopiedId(null);
  };

  // Copy to clipboard
  const handleCopy = (text, langCode) => {
    navigator.clipboard.writeText(text);
    setCopiedId(langCode);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const fromLang = getLanguageInfo(fromLanguage);
  const availableToAdd = getAvailableLanguages();

  // Split translations into rows of 2 (for display)
  const getTranslationRows = () => {
    const rows = [];
    // Create pairs: [original, lang1], [lang2, lang3], [lang4, lang5]...
    for (let i = 0; i < toLanguages.length; i += 2) {
      rows.push({
        left: toLanguages[i],
        right: toLanguages[i + 1] || null
      });
    }
    return rows;
  };

  const translationRows = getTranslationRows();

  return (
    <div className="trans-container">
      <h1 className="sr-only">Language Translator</h1>

      <div className="trans-wrapper">
        {/* Header */}
        <div className="trans-header">
          <h1 className="trans-title">✨ Language Translator</h1>
          <p className="trans-subtitle">Translate text to multiple languages instantly</p>
        </div>

        {/* Main Card */}
        <div className="trans-main-card">
          {/* Selector Section */}
          <div className="trans-selector-section">
            {/* From and To Language Selectors */}
            <div className="trans-selector-grid">
              {/* From Language */}
              <div className="trans-selector-group">
                <label className="trans-selector-label">
                  <span className="trans-selector-label-icon">📤</span>
                  From Language
                </label>
                <select
                  value={fromLanguage}
                  onChange={(e) => setFromLanguage(e.target.value)}
                  className="trans-selector-select"
                >
                  {languages.map((lang) => (
                    <option key={lang.code} value={lang.code}>
                      {lang.flag} {lang.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* To Language - Main Selector */}
              <div className="trans-selector-group">
                <label className="trans-selector-label">
                  <span className="trans-selector-label-icon">📥</span>
                  To Language
                </label>
                <select
                  onChange={(e) => {
                    if (e.target.value) {
                      addLanguage(e.target.value);
                      e.target.value = '';
                    }
                  }}
                  className="trans-selector-select"
                >
                  <option value="">+ Add language...</option>
                  {availableToAdd.map((lang) => (
                    <option key={lang.code} value={lang.code}>
                      {lang.flag} {lang.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Selected Languages Tags */}
            {toLanguages.length > 0 && (
              <div className="trans-to-languages">
                <label className="trans-to-languages-label">
                  Selected languages: ({toLanguages.length})
                </label>
                <div className="trans-languages-tags">
                  {toLanguages.map((langCode) => {
                    const lang = getLanguageInfo(langCode);
                    return (
                      <div key={langCode} className="trans-language-tag">
                        <span>{lang.flag} {lang.name}</span>
                        <button
                          className="trans-language-tag-remove"
                          onClick={() => removeLanguage(langCode)}
                          aria-label={`Remove ${lang.name}`}
                          title={`Remove ${lang.name}`}
                        >
                          ✕
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Input Text Area */}
            <div className="trans-selector-group" style={{ marginBottom: '2rem' }}>
              <label className="trans-selector-label" htmlFor="input-text">
                <span className="trans-selector-label-icon">📝</span>
                Text to Translate
              </label>
              <textarea
                id="input-text"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder="Enter text here..."
                className="trans-input"
              />
            </div>

            {/* Buttons */}
            <div className="trans-buttons-group">
              <button
                onClick={handleConvert}
                disabled={loading}
                className="trans-btn trans-btn-primary"
              >
                {loading ? (
                  <>
                    <span className="trans-loader"></span>
                    Converting...
                  </>
                ) : (
                  <>🚀 Convert</>
                )}
              </button>
              <button
                onClick={handleClear}
                disabled={!inputText && !hasConverted}
                className="trans-btn trans-btn-secondary"
              >
                🗑️ Clear
              </button>
            </div>
          </div>

          {/* Results Section - 2 Column Layout */}
          {hasConverted && (
            <div style={{
              padding: '2.5rem',
              borderTop: '2px solid var(--border-light)',
              backgroundColor: 'var(--bg-secondary)'
            }}>
              <div className="trans-results-section">
                {/* Row 1: Original + First Translation */}
                <div className="trans-result-row">
                  {/* Original Text Column */}
                  <div className="trans-result-col">
                    <h2 className="trans-result-column-title">
                      {fromLang?.flag} {fromLang?.name} (Original)
                    </h2>
                    <div className="trans-input-preview">
                      {inputText || '(empty)'}
                    </div>
                  </div>

                  {/* First Translation Column */}
                  {toLanguages[0] && (
                    <div className="trans-result-col">
                      {(() => {
                        const lang = getLanguageInfo(toLanguages[0]);
                        const translatedText = translations[toLanguages[0]] || '';
                        return (
                          <>
                            <h2 className="trans-result-column-title">
                              {lang?.flag} {lang?.name}
                            </h2>
                            <div className="trans-translation-wrapper">
                              <p className="trans-translation-text">
                                {translatedText || 'Translating...'}
                              </p>
                              <button
                                className={`trans-copy-btn ${copiedId === toLanguages[0] ? 'copied' : ''}`}
                                onClick={() => handleCopy(translatedText, toLanguages[0])}
                                disabled={!translatedText || translatedText === 'Translating...'}
                                title="Copy translation"
                              >
                                {copiedId === toLanguages[0] ? '✓ Copied' : 'Copy'}
                              </button>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  )}
                </div>

                {/* Remaining translations in pairs (2 per row) */}
                {toLanguages.slice(1).map((langCode, index) => {
                  // Show paired with next language if exists
                  if (index % 2 === 0) {
                    const nextLang = toLanguages[index + 2]; // +2 because we sliced from index 1
                    const currentLangInfo = getLanguageInfo(langCode);
                    const nextLangInfo = nextLang ? getLanguageInfo(nextLang) : null;
                    const currentText = translations[langCode] || '';
                    const nextText = nextLang ? translations[nextLang] || '' : null;
                    
                    return (
                      <div key={langCode} className="trans-result-row">
                        {/* Left Column */}
                        <div className="trans-result-col">
                          <h2 className="trans-result-column-title">
                            {currentLangInfo?.flag} {currentLangInfo?.name}
                          </h2>
                          <div className="trans-translation-wrapper">
                            <p className="trans-translation-text">
                              {currentText || 'Translating...'}
                            </p>
                            <button
                              className={`trans-copy-btn ${copiedId === langCode ? 'copied' : ''}`}
                              onClick={() => handleCopy(currentText, langCode)}
                              disabled={!currentText || currentText === 'Translating...'}
                              title="Copy translation"
                            >
                              {copiedId === langCode ? '✓ Copied' : 'Copy'}
                            </button>
                          </div>
                        </div>
                        
                        {/* Right Column (if exists) */}
                        {nextLang && (
                          <div className="trans-result-col">
                            <h2 className="trans-result-column-title">
                              {nextLangInfo?.flag} {nextLangInfo?.name}
                            </h2>
                            <div className="trans-translation-wrapper">
                              <p className="trans-translation-text">
                                {nextText || 'Translating...'}
                              </p>
                              <button
                                className={`trans-copy-btn ${copiedId === nextLang ? 'copied' : ''}`}
                                onClick={() => handleCopy(nextText, nextLang)}
                                disabled={!nextText || nextText === 'Translating...'}
                                title="Copy translation"
                              >
                                {copiedId === nextLang ? '✓ Copied' : 'Copy'}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  }
                  return null;
                })}
              </div>
            </div>
          )}

          {/* Empty State */}
          {!hasConverted && !inputText && (
            <div className="trans-empty-state" style={{ padding: '3rem 2rem' }}>
              <div className="trans-empty-icon">🌍</div>
              <p className="trans-empty-text">
                Select languages and enter text to start translating
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}